/*
  IRON PLATE storefront behaviour.
  1. Add to cart: forms marked [data-ip-quick-add] post to /cart/add without JavaScript. With
     Shopify's cart actions available, the add happens in place and, for [data-ip-open-cart] forms,
     the cart drawer opens; the theme hears the standard cart event and refreshes the cart.
  2. Buy box gallery: thumbnails swap the main image.
  3. Sticky buy bar on phones: shown once the main buy button has scrolled out of view.
*/
(() => {
  /* ---------- 1. Add to cart ---------- */

  /** @param {HTMLFormElement} form @param {string} text */
  const announce = (form, text) => {
    const status = form.querySelector('[data-ip-status]');
    if (status) status.textContent = text;
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ip-quick-add')) return;

    const actions = window.Shopify && window.Shopify.actions;
    if (!actions || typeof actions.updateCart !== 'function') return; // native post to /cart/add

    event.preventDefault();

    const opensCart = form.hasAttribute('data-ip-open-cart');
    const idInput = form.querySelector('input[name="id"]');
    const buttons = [
      ...form.querySelectorAll('button[type="submit"]'),
      ...(form.id ? document.querySelectorAll(`button[type="submit"][form="${form.id}"]`) : []),
    ].filter((b) => b instanceof HTMLButtonElement);
    const label = form.querySelector('[data-ip-label]');
    if (!(idInput instanceof HTMLInputElement) || buttons.length === 0) return;
    if (buttons.some((b) => b.disabled) || (!opensCart && buttons[0].dataset.state === 'added')) return;

    buttons.forEach((b) => {
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
    });

    try {
      const result = await actions.updateCart(
        { lines: [{ merchandiseId: idInput.value, quantity: 1 }] },
        { event: { context: 'standard-action' } }
      );
      if (result && Array.isArray(result.userErrors) && result.userErrors.length > 0) {
        throw new Error(result.userErrors[0].message);
      }
      const addedText = form.dataset.addedLabel || 'נוסף לעגלה';
      announce(form, addedText);

      if (opensCart) {
        buttons.forEach((b) => {
          b.disabled = false;
        });
        if (typeof actions.openCart === 'function') {
          try {
            await actions.openCart();
          } catch (_) {
            window.location.href = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : '/cart';
          }
        } else {
          const drawer = document.getElementById('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
          else window.location.href = '/cart';
        }
      } else {
        if (label) label.textContent = addedText;
        buttons[0].dataset.state = 'added';
      }
    } catch (error) {
      buttons.forEach((b) => {
        b.disabled = false;
      });
      const message = error instanceof Error && error.message ? error.message : 'לא הצלחנו להוסיף לעגלה. נסו שוב.';
      announce(form, message);
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  });

  /* ---------- 2. Buy box gallery ---------- */

  document.addEventListener('click', (event) => {
    const thumb = event.target instanceof Element ? event.target.closest('[data-ipx-thumb]') : null;
    if (!(thumb instanceof HTMLButtonElement)) return;
    const gallery = thumb.closest('[data-ipx-gallery]');
    const main = gallery && gallery.querySelector('[data-ipx-main]');
    if (!(main instanceof HTMLImageElement)) return;

    main.srcset = thumb.dataset.srcset || '';
    main.src = thumb.dataset.src || main.src;
    main.alt = thumb.dataset.alt || '';
    gallery.querySelectorAll('[data-ipx-thumb]').forEach((t) => t.removeAttribute('aria-current'));
    thumb.setAttribute('aria-current', 'true');
  });

  /* ---------- 3. Sticky buy bar ---------- */

  const initSticky = () => {
    const bar = document.querySelector('[data-ipx-sticky]');
    const anchor = document.querySelector('[data-ipx-buy-btn]');
    if (!bar || !anchor) return;

    let on = false;
    let queued = false;
    const update = () => {
      queued = false;
      // Show only once the main button has scrolled up past the top of the screen.
      const next = anchor.getBoundingClientRect().bottom < 0;
      if (next === on) return;
      on = next;
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (on) bar.removeAttribute('inert');
      else bar.setAttribute('inert', '');
    };
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    update();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSticky);
  else initSticky();
})();
